import { FC, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import { colors } from '../../const/colors'

export const CARD_SX = {
  background: colors.surface,
  border: `1px solid ${colors.border2}`,
  borderRadius: colors.radiusCard,
  boxShadow: colors.shadowCard,
  p: '22px',
} as const

export const FIELD_SX = {
  height: 48,
  px: '16px',
  border: `1.5px solid ${colors.border3}`,
  borderRadius: '12px',
  fontSize: '14.5px',
  fontWeight: 700,
  color: colors.navy,
} as const

export const LABEL_SX = {
  fontSize: '11px',
  color: colors.slate,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  mb: '4px',
} as const

export const NO_PROFILE = 'no_profile'

export const SectionHeader: FC<{
  icon: ReactNode
  title: string
  mb?: string
  action?: ReactNode
  step?: number
  done?: boolean
}> = ({ icon, title, mb = '18px', action, step, done }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', mb }}>
    <Box
      data-e2e={step ? `step-${step}` : undefined}
      data-done={step ? !!done : undefined}
      sx={{
        width: 34,
        height: 34,
        borderRadius: step ? '50%' : '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: '14px',
        fontWeight: 800,
        transition: 'background .2s, color .2s',
        ...(done
          ? { background: colors.green, color: '#fff' }
          : {
              background: step ? colors.rowSelected : 'rgba(19,54,73,0.07)',
              color: colors.navy,
            }),
      }}
    >
      {step ? done ? <CheckIcon sx={{ fontSize: 19 }} /> : step : icon}
    </Box>
    <Typography
      sx={{
        fontSize: '15px',
        fontWeight: 800,
        letterSpacing: '-0.01em',
        flex: 1,
      }}
    >
      {title}
    </Typography>
    {action}
  </Box>
)

export const SubTitle: FC<{ children: ReactNode; sx?: object }> = ({
  children,
  sx,
}) => (
  <Typography
    sx={{
      fontSize: '13.5px',
      fontWeight: 800,
      color: colors.navy,
      mb: '10px',
      ...sx,
    }}
  >
    {children}
  </Typography>
)

export const InfoField: FC<{
  label: string
  value?: string
  full?: boolean
}> = ({ label, value, full }) => (
  <Box sx={{ gridColumn: full ? '1 / -1' : undefined }}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Typography
      sx={{
        fontSize: '14.5px',
        fontWeight: 700,
        color: colors.navy,
        wordBreak: full ? 'break-all' : undefined,
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
)

export const CloseCircle: FC<{
  onClick: () => void
  size?: number
  e2e?: string
}> = ({ onClick, size = 36, e2e }) => (
  <Box
    onClick={onClick}
    data-e2e={e2e}
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: '#EFF3F6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: colors.textSecondary,
      flexShrink: 0,
      transition: 'background .15s',
      '&:hover': { background: '#E1E9EE' },
    }}
  >
    <CloseIcon sx={{ fontSize: size / 2 }} />
  </Box>
)
